<template>
  <div class="vcl-pagination">
    <div class="vcl-pagination__select" v-show="allowPageSizeUpdates">
      <label :for="pageSizeSelectId" aria-label="Items Per Page">{{ $t('show') }}:</label>
      <select :id="pageSizeSelectId" v-model="pageSize" name="paginator-select" aria-label="Select items per page" @change="($event) => updatePageSize($event)">
        <option v-for="(size, index) in pageSizeList" :key="index" :value="size">{{ size }}</option>
      </select>
    </div>
    <ul v-if="lastPage > 1" class="vcl-pagination__list">
      <li class="vcl-pagination__list-arrow">
        <VpIcon icon="chevron-left" class="vcl-pagination__prev" :class="{ 'is-arrow-disabled': page === 0 }" tabindex="0" aria-label="Previous Page" role="button" @click="prevPageClicked" @keydown.enter="prevPageClicked" />
      </li>
      <li v-for="(item, index) in pageList" :key="index" class="vcl-pagination__list-item" :class="item.class || ''">
        <a
          :class="item.class"
          :tabindex="item.tabindex"
          :aria-label="`Page ${item.text}`"
          @click="item.click(item)"
          @keydown.enter="item.click(item)"
        >
          {{ item.text }}
        </a>
      </li>
      <li class="vcl-pagination__list-arrow">
        <VpIcon icon="chevron-right" class="vcl-pagination__next" :class="{ 'is-arrow-disabled': page === (lastPage - 1) }" tabindex="0" aria-label="Next Page" role="button" @click="nextPageClicked" @keydown.enter="nextPageClicked" />
      </li>
    </ul>
  </div>
</template>

<script>
import VpIcon from '../vp-icon/VpIcon.vue';

export default {
  name: 'VpTablePagination',
  components: {
    VpIcon,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    initialPage: {
      type: Number,
      default: 0,
    },
    initialPageSize: {
      type: Number,
      default: 0
    },
    pageSizeList: {
      type: Array,
      default: () => [],
    },
    lastPage: {
      type: Number,
      default: 0,
    },
    allowPageSizeUpdates: {
      type: Number,
      default: 1,
    },
  },
  emits: ['updated'],
  data() {
    return {
      page: 0,
      pageSize: 25,
    };
  },
  computed: {
    pageList() {
      return this.makePageList(this.page, this.lastPage);
    },
    pageSizeSelectId() {
      return `pageSizeSelectId-${this._uid}`;
    }
  },
  created() {
    this.page = this.initialPage;
    this.pageSize = this.initialPageSize;
  },
  watch: {
    initialPage(value) {
      this.page = value;
    },
    initialPageSize(value) {
      this.pageSize = value;
    },
  },
  methods: {
    updatePageSize($event) {
      const { target: { value = null }} = $event;
      this.page = 0;
      this.$emit('updated', { pageSize: Number(value) });
    },
    transport(page, force = false) {
      if (page === this.page && force === false) {
        return null;
      }
      if (page >= 0 && page <= this.lastPage) {
        this.page = page > this.lastPage ? this.lastPage : page;
        this.$emit('updated', { page: this.page });
      }
      return null;
    },
    getPage: function getPage(item) {
      return this.transport(item.page);
    },
    getMore: function getMore(item) {
      // TODO: Do something when '...' is clicked.
      console.log(item);
    },
    makePageList: function makePageList(page, lastPage) {
      return Array(lastPage).fill()
        .map((x, i) => ({ page: i, tabindex: 0 }))
        .map((item, k) => {
          const show = (k === page || (page + 1) === k || (page - 1) === k || k === 0 || k === 1 || k === lastPage || k === (lastPage - 1));
          return { 
            ...item, 
            class: item.page === page ? 'selected' : '', 
            text: `${item.page + 1}`, 
            click: this.getPage,
            show,
          };
         })
        .reduce((prev, next) => {
          if (next.show) {
            return prev.concat(next);
          }
          if (prev.slice(-1)[0] && prev.slice(-1)[0].show) {
            return prev.concat({
              ...next, class: 'more', click: this.getMore, text: '...', tabindex: -1
            });
          }
          return prev;
        }, []);
    },
    nextPageClicked() {
      return this.page !== (this.lastPage - 1) && this.transport(this.page + 1);
    },
    prevPageClicked() {
      return this.page !== 0 && this.transport(this.page - 1);
    },
  },
};
</script>

<style lang="scss">
  .vcl-pagination {
    $self: &;
    display: flex;
    &__select {
      font-size: 1rem;
      select {
        width: 70px;
        text-align: center;
        background-color: white;
        border-radius: 4px;
        border: 1px solid #DBDBDB;
        font-size: 1rem;
        height: 30px;
        padding: 0 10px 0 10px;
        margin: 0;
        margin-left: 5px;
      }

      label {
        display: inline-block;
        font-size: 16px;
      }
    }
    &__list {
      border: 1px solid #dbdbdb;
      border-radius: 4px;
      padding: 0;
      margin: 0;
      margin-left: 5px;
      display: flex;
      &-item {
        padding-top: 0px;
      }
      &-item, &-arrow {
        cursor: pointer;
        text-decoration: none;
        display: flex;
        a {
          line-height: 1.5rem;
          color: #016699;
          font-weight: normal;
          font-size: 16px;
          text-decoration: none;
          margin: 2px;
          &.selected {
            color: #4F4F4F;
            font-weight: bold;
            cursor: default;
          }
        }
        #{ $self }__prev {
            font-size: 10px;
            padding: 0;
            margin-top: 2px;
            color: #4F4F4F;
            &.is-arrow-disabled {
              color: #DBDBDB;
            }
          }
          #{ $self }__next {
            font-size: 10px;
            padding: 0;
            margin-top: 2px;
            color: #4F4F4F;
            &.is-arrow-disabled {
              color: #DBDBDB;
            }
          }
      }
      &-arrow {
        margin: 7px;
        padding: 0;
      }
    }
  }
</style>
